import type {SData, SDataRelatedOptions} from './types';
import SliderData from "./SliderData";
import Config from "./config";
import ColorizerStorage from './ColorizerStorage';
import Checkbox from "../Checkbox/Checkbox";
import {sliderTelematicsColorize} from "./sliderTelematicsColorize";
import {featureQuantile} from "./featureQuantile";
import type {QuantileResponseType} from "./featureQuantile";
import {Spinner} from "../Spinner/Spinner";
import {featureAllowUserToSelectSubMenuOptions} from "./featureAllowUserToSelectSubMenuOptions";
import {featureAllowUserToSelectDatePeriod} from "./featureAllowUserToSelectDatePeriod";
import {featureGetHumanDatesForGivenPeriod} from "../../features/featureGetHumanDatesForGivenPeriod";
import {featureGetRelatedRange} from "./featureGetRelatedRange";

interface SliderVisibleExtremes {
    without_filter_min: number;
    without_filter_max: number;
    // with_filter_min: number;
    // with_filter_max: number;
}

type ModalColorizerSetupSliderCallbacks = {
    onSaveSlider: (newSliderData: SData) => void;
    onChangeSelectedType: (newSelectedType: string, resetSliderUserPeriods: boolean) => void;
}


interface ModalColorizerSetupSlider {
    callbacks: ModalColorizerSetupSliderCallbacks;
    root: HTMLDivElement;
    footer: HTMLDivElement;
    useFilter: boolean;
    selectedType: string;
    sliderTelematicsColorize: sliderTelematicsColorize;
    visibleExtremes: SliderVisibleExtremes;
    niceColorsTable: string[];
    sliderWidth: number;
    originalFilter: any;
    getElement(): HTMLDivElement;
    setSelectedType(type: string): void;
    setSliderData(sData: SData): void;
}


class ModalColorizerSetupSlider implements ModalColorizerSetupSlider {
    
    constructor(originalFilter: any, selectedType: string, callbacks: ModalColorizerSetupSliderCallbacks) {
        this.callbacks = callbacks;

        this.root = document.createElement('div') as HTMLDivElement;
        this.root.innerHTML = this.getHTML();
        
        this.originalFilter = originalFilter;

        this.sliderWidth = 600;

        this.selectedType = selectedType;

        this.visibleExtremes = {
            without_filter_min: 0,
            without_filter_max: 100,
            // with_filter_min: 0,
            // with_filter_max: 100,
        };

        this.niceColorsTable = Config.analyticsColorsShort;
        
        this.sliderTelematicsColorize = this.createSliderInstance(
            -123,
            [{value: 123, color: '#ff0000'}],
            '',
            ColorizerStorage.getIsSmartRoundingEnabled(),
            {
                date_first: null, //'2022-02',
                date_second: null,
                compare_dates: false,
                related_collection: null,
            }
        );
        
        this.reloadFooter();
    }

    createSliderInstance(
        min: number,
        blocks: { value: number; color: string; }[],
        unit: string,
        make_smooth_numbers: boolean,
        related_options: SDataRelatedOptions
    ): sliderTelematicsColorize {
        let slider: sliderTelematicsColorize = new sliderTelematicsColorize(
            this.root.querySelector('#telematics-colorize-slider'),
            {
                min,
                blocks,
                indicatorColor: '#6c6b6a',
                roundValues: false,
                hiddenInputsOutsideMinMax: true,
                unit,
                make_smooth_numbers
            },
            {
                onSliderChange: (currentValue1: number | false, currentValue2: number | false) => {
                    console.log('ModalColorizerSetupSlider, onSliderChange', currentValue1, currentValue2);
                }
            }
        );
        // console.log('ModalColorizerSetupSlider, createSliderInstance, related_options', related_options);
        slider.getSliderData().setRelatedOptions(related_options);
        return slider;
    }

    setSliderData(sData: SData) {
        // console.debug('ModalColorizerSetupSlider, setSliderData', sData);
        let blocks = sData.blocks.map(b => {
            return {
                value: b.max,
                color: b.color,
            }
        });
        this.sliderTelematicsColorize = this.createSliderInstance(
            sData.visible_min,
            blocks,
            sData.unit,
            ColorizerStorage.getIsSmartRoundingEnabled(),
            sData.related_options
        );
        let reloadOK: boolean = this.sliderTelematicsColorize.reload();
        this.reloadSubMenuOptions(sData.selected_option);
    }

    setQuantileActionsEnabled(enabled: boolean) {
        if (enabled) {
            this.root.querySelectorAll('.additional-actions').forEach((el: HTMLButtonElement) => {
                el.disabled = false;
                el.style.opacity = '1';
            });
        } else {
            this.root.querySelectorAll('.additional-actions').forEach((el: HTMLButtonElement) => {
                el.disabled = true;
                el.style.opacity = '0.5';
            });
        }
    }
    
    reloadSubMenuOptions(selectedType: string): void {
        this.selectedType = selectedType;
        // console.debug('EEE, reloadSubMenuOptions, selectedType, getSubMenu, getAvailableTypeByName',
        //     selectedType,
        //     this.originalFilter.getSubMenu(),
        //     this.originalFilter.getAvailableTypeByName(this.selectedType)
        // );
        let filterElement = this.originalFilter.getAvailableTypeByName(this.selectedType);
        let optionsContainer: HTMLDivElement = this.root.querySelector('.select-sub-menu-options-for-slider') as HTMLDivElement;
        optionsContainer.style.display = 'flex';
        if (filterElement.can_be_related) {
            this.setQuantileActionsEnabled(false);

            let fld = this.originalFilter.getAvailableTypeByName(this.selectedType);

            // console.log('ModalColorizerSetupSlider, featureAllowUserToSelectDatePeriod, fld', fld);
            new featureAllowUserToSelectDatePeriod(
                optionsContainer,
                this.selectedType,
                this.sliderTelematicsColorize.getSliderData().getRelatedOptions(),
                featureGetHumanDatesForGivenPeriod(fld.period_range.min, fld.period_range.max),
                {
                    onChange: (relatedOptions: SDataRelatedOptions) => {
                        this.sliderTelematicsColorize.getSliderData().setRelatedOptions(relatedOptions);
                        console.debug('ModalColorizerSetupSlider, featureAllowUserToSelectDatePeriod, options', relatedOptions);
                        featureGetRelatedRange.getRange({
                            f: this.selectedType,
                            r: relatedOptions.related_collection,
                            p: relatedOptions.date_first + (relatedOptions.compare_dates ? '/' + relatedOptions.date_second : '/'),
                            fp: 'c_period',
                        }).then((data) => {
                            this.sliderTelematicsColorize.getSliderData().setVisibleMinMax(data.range.min, data.range.max);
                            this.sliderTelematicsColorize.getSliderData().truncateBlocksToBeWithinVisibleMinMax();
                            this.sliderTelematicsColorize.getSliderData().createProportionalBlocks(5, this.niceColorsTable);
                            this.sliderTelematicsColorize.reload();
                            this.setLoadingMode(false, true);
                        }).catch(error => {
                            console.warn('onChangeUseFilter', error);
                            this.setUseFilter(this.useFilter, 0, 0);
                            this.setLoadingMode(false, false);
                        }).finally(() => {
                        });
                        // this.callbacks.onChangeSelectedType(this.selectedType);
                    }
                }
            );
        } else {
            this.setQuantileActionsEnabled(true);
            new featureAllowUserToSelectSubMenuOptions(
                optionsContainer,
                this.selectedType,
                this.originalFilter.getSubMenu(),
                {
                    onChange: (selectedOption: string) => {
                        console.debug('ModalColorizerSetupSlider, featureAllowUserToSelectSubMenuOptions, onChange', selectedOption);
                        this.setSelectedType(selectedOption);
                        this.callbacks.onChangeSelectedType(selectedOption, false);
                    }
                }
            );
        }
    }

    setDefaultData(selectedOption: string, min: number, max: number, unit: string, related_options: SDataRelatedOptions, resetSliderUserPeriods: boolean): boolean {
        // let lastPossiblePeriod = this.originalFilter.getAvailableTypeByName(selectedOption)?.period_range?.max ?? '2021-12';
        this.setSelectedType(selectedOption, true);
        if (resetSliderUserPeriods) {
            this.sliderTelematicsColorize = this.createSliderInstance(
                min,
                [{value: max, color: '#ff0000'}],
                unit,
                ColorizerStorage.getIsSmartRoundingEnabled(),
                related_options
            );
            this.sliderTelematicsColorize.getSliderData().createProportionalBlocks(5, this.niceColorsTable);
        } else {
            this.sliderTelematicsColorize.getSliderData().setVisibleMinMax(min, max, false);
            this.sliderTelematicsColorize.getSliderData().sortBlocksByMax();
            this.sliderTelematicsColorize.getSliderData().setMinimum(this.sliderTelematicsColorize.getSliderData().getVisibleMin());
            // this.sliderTelematicsColorize.getSliderData().truncateBlocksToBeWithinVisibleMinMax(); // #FIXME: remove this line, but fix periods visuality on slider after change selection
        }
        let reloadOK = this.sliderTelematicsColorize.reload();
        this.reloadSubMenuOptions(selectedOption);
        return reloadOK;
    }
    
    setLoadingMode( loadingMode: boolean, withSuccess: boolean = true) {
        if (loadingMode) {
            console.debug('ModalColorizerSetupSlider, setLoadingMode, true');
            (this.root.querySelector('.telematics-colorize_footer') as HTMLDivElement).style.visibility = 'hidden';
            (this.root.querySelector('.select-sub-menu-options-for-slider') as HTMLDivElement).style.visibility = 'hidden';
            
            (new Spinner(this.root.querySelector('.telematics-colorize_body') as HTMLDivElement)).show();
        } else {
            console.debug('ModalColorizerSetupSlider, setLoadingMode, false');
            if (withSuccess) {
                (this.root.querySelector('.telematics-colorize_footer') as HTMLDivElement).style.visibility = 'visible';
                (this.root.querySelector('.select-sub-menu-options-for-slider') as HTMLDivElement).style.visibility = 'visible';
            }
            (new Spinner(this.root.querySelector('.telematics-colorize_body') as HTMLDivElement)).hide();
        }
    }

    getSliderData(): SData {
        return this.sliderTelematicsColorize.getSliderData().getSliderData();
    }

    setSelectedType(type: string, createAutoPeriods: boolean = false) {
        this.selectedType = type;
        this.sliderTelematicsColorize.getSliderData().setTypeName(this.selectedType);
        
        this.sliderTelematicsColorize.getSliderData().setMinimum(this.visibleExtremes.without_filter_min);
        this.sliderTelematicsColorize.getSliderData().setVisibleMinMax(this.visibleExtremes.without_filter_min, this.visibleExtremes.without_filter_max);

        if (this.sliderTelematicsColorize.getSliderData().getBlocks().length === 0 || createAutoPeriods && false) {
            console.log('ModalColorizerSetupSlider, Creating default slider for speed and 5 colors');
            this.sliderTelematicsColorize.getSliderData().createProportionalBlocks(5, this.niceColorsTable);
        }

        this.sliderTelematicsColorize.reload();
    }

    setUseFilter(useFilter: boolean, min: number, max: number): boolean {
        this.sliderTelematicsColorize.getSliderData().setUseFilter(useFilter);
        this.sliderTelematicsColorize.getSliderData().setVisibleMinMax(min, max);
        // this.sliderTelematicsColorize.getSliderData().truncateBlocksToBeWithinVisibleMinMax();
        return this.sliderTelematicsColorize.reload();
    }

    reloadFooter(): void {
        let footer = this.root.querySelector('.telematics-colorize_footer');
        footer.innerHTML = this.getFooterHTML();

        this.root.querySelector('.js-auto').addEventListener('click', (e) => {
            this.sliderTelematicsColorize.getSliderData().createProportionalBlocks(5, this.niceColorsTable);
            this.sliderTelematicsColorize.reload();
        });

        this.root.querySelector('.js-reversed-color-scale').addEventListener('click', (e: Event) => {
            this.sliderTelematicsColorize.getSliderData().setReversedColorScale(this.sliderTelematicsColorize.getSliderData().getReversedColorScale());
            this.sliderTelematicsColorize.reload();
        });

        this.root.querySelector('.js-save').addEventListener('click', (e) => {
            this.sliderTelematicsColorize.getSliderData().truncateBlocksToBeWithinVisibleMinMax();
            this.sliderTelematicsColorize.getSliderData().updateValuesToBeSmoothLikeLabels();
            this.callbacks.onSaveSlider(this.sliderTelematicsColorize.getSliderData().getSliderData());
        });

        (this.root.querySelector('.js-smart-rounding') as HTMLDivElement).appendChild(
            Checkbox({
                checked: ColorizerStorage.getIsSmartRoundingEnabled(),
                label: 'Zaokrąglaj',
                mode: 'black-checkbox',
                disabled: false
            }, {
                onChange: (checked: boolean) => {
                    ColorizerStorage.setIsSmartRoundingEnabled(checked);
                    this.sliderTelematicsColorize.reload(ColorizerStorage.getIsSmartRoundingEnabled());
                }
            }).element
        );

        function getQuantileOnClick( quantileNumber: number) {
            return (sliderData: SliderData) => {
                featureQuantile.getQuantile({
                    f: this.selectedType,
                    d: quantileNumber,
                    r: '',
                    p: false,
                    filter: sliderData.getUseFilter(),
                    c_min: sliderData.getVisibleMin(),
                    c_max: sliderData.getVisibleMax(),
                }).then((data) => {
                    this.reloadSliderWith(data);
                    this.setLoadingMode(false, true);
                }).catch(error => {
                    console.warn('onChangeUseFilter', error);
                    this.setUseFilter(this.useFilter, 0, 0);
                    this.setLoadingMode(false, false);
                }).finally(() => {
                });
            };
        }

        const additionalActions: { label: string, onClick: (sliderData: SliderData) => void }[] = [
            {
                label: 'Mediana<span style="font-size: 10px; font-weight: 800; margin-left: 7px;">/2</span>',
                onClick: getQuantileOnClick.call(this, 2)
            },
            {
                label: 'Kwartyle<span style="font-size: 10px; font-weight: 800; margin-left: 7px;">/4</span>',
                onClick: getQuantileOnClick.call(this, 4),
            },
            {
                label: 'Kwintyle<span style="font-size: 10px; font-weight: 800; margin-left: 7px;">/5</span>',
                onClick: getQuantileOnClick.call(this, 5),
            }
        ]

        let additionalButtons = this.root.querySelector('.additional-buttons');
        additionalButtons.innerHTML = '';
        additionalActions.forEach((a) => {
            let button = document.createElement('button');
            button.classList.add('button', 'button_small', 'button-secondary', 'additional-actions');
            button.style.minWidth = '70px';
            button.innerHTML = a.label;
            button.addEventListener('click', (e) => {
                // console.debug('getQuantile, additionalButtons', a.label, this.sliderTelematicsColorize.getSliderData().getSliderData());
                // console.debug('getQuantile, additionalButtons, useFilter', this.sliderTelematicsColorize.getSliderData().getUseFilter());
                this.setLoadingMode(true);
                a.onClick(this.sliderTelematicsColorize.getSliderData());
            });
            additionalButtons.appendChild(button);
        });
    }

    reloadSliderWith(data: QuantileResponseType): void {
        this.sliderTelematicsColorize.getSliderData().removeBlocks();
        data.v.forEach((v) => {
            this.sliderTelematicsColorize.getSliderData().addBlock(v, this.niceColorsTable[this.sliderTelematicsColorize.getSliderData().getBlocks().length]);
        });
        this.sliderTelematicsColorize.getSliderData().addBlock(data.v_max, this.niceColorsTable[this.sliderTelematicsColorize.getSliderData().getBlocks().length]);
        this.sliderTelematicsColorize.getSliderData().setVisibleMinMax(data.v_min, data.v_max);
        this.sliderTelematicsColorize.reload();
        // console.debug('reloadSliderWith', data);
    }

    getFooterHTML(): string {
        return `
            <div style="display: flex; align-items: center; justify-content: space-between;">
                <!-- <span>Podział:</span> -->
                <button class="js-reversed-color-scale button button_small button-secondary button_white" style="min-width: 90px; ">Odwróć kolory</button>
                <button class="js-auto button button_small button-secondary button_white" style="min-width: 50px; ">Podział auto</button>
                <div class="additional-buttons" style="display: flex;">
                </div>
                <div class="js-smart-rounding" style="display: block;"></div>
            </div>
            <div style="width: 100px;">
                <button class="js-save button button_black button-primary">Zastosuj</button>
            </div>
        `;
    }

    getElement(): HTMLDivElement {
        return this.root;
    }

    getHTML(): string {
        return `
            <div class="telematics-colorize_body">
                <div id="colorize_spacer" style="
                height: 200px;
                width: 700px;
                display: flex;
                align-items: center;
                justify-content: space-evenly;
                flex-direction: column;
                row-gap: 25px;
                padding: 18px 30px 50px 30px;">
                    <div class="select-sub-menu-options-for-slider" style=" width: 100%;  padding: 0 20px; align-items: center; column-gap: 10px;"></div>
                    <div id="telematics-colorize-slider" style="margin-top: 20px; visibility: visible; width: 600px; position: relative; height: auto; min-height: 30px;">
                    </div>
                </div>
            </div>

            <div class="telematics-colorize_footer">
            </div>
            `;
    }

}

export default ModalColorizerSetupSlider;
